import { api } from '../../core/network/api';

/**
 * The base weapon skins slice of the loadout catalogue — E5's flat 900-gem finish, one per
 * weapon (`shared/src/weapon-skins.ts`: `BASE_WEAPON_SKINS`, ids `10_000 + weaponId`).
 *
 * `GET /game/loadout` already returns these mixed into `weaponSkins` alongside the stride-
 * ladder finishes (`LoadoutService.catalogue()`); this file reads only the fields the Shop's
 * base-skins section needs rather than pulling in the full loadout-editor contract
 * (`loadouts`, `slots`, `weapons`), which is `armoury`/`inventory`'s to own.
 *
 * There is no `equipped` flag here — a finish is worn per loadout slot, not globally, so the
 * shop can only say "owned" and point at the Armoury to wear it (same pattern the character
 * skins above it already use for their own gated/owned entries).
 */
export interface WeaponSkinEntry {
  id: number;
  weaponId: number;
  name: string;
  /** `#rrggbb`, formatted server-side. */
  tint: string;
  priceCoins: number;
  currency: 'SOFT' | 'PREMIUM';
  owned: boolean;
}

export interface WeaponSkinsCatalogueView {
  weaponSkins: WeaponSkinEntry[];
  progress: { level: number; softBalance: number; premiumBalance: number };
}

/** id >= this offset is a base finish (one per weapon); below it is a stride-ladder finish
 * already sold elsewhere in the shell. Mirrors `isBaseWeaponSkinId`
 * (`shared/src/weapon-skins.ts`) — duplicated rather than imported because the frontend has no
 * build wiring to `shared/` outside `engine/arena`'s prediction code, the same reason the
 * backend mirrors its cosmetic catalogues into `*.types.ts` instead of importing them. */
export const BASE_WEAPON_SKIN_ID_OFFSET = 10_000;

export function isBaseWeaponSkinId(id: number): boolean {
  return Number.isInteger(id) && id >= BASE_WEAPON_SKIN_ID_OFFSET;
}

/** `GET /game/loadout` — read for its `weaponSkins` + `progress` fields only. */
export const fetchWeaponSkinsCatalogue = () => api.get<WeaponSkinsCatalogueView>('/game/loadout');

/** `POST /game/arsenal/skins/purchase` — the same route the 15 stride-ladder finishes already
 * buy through (`ArsenalController`). */
export const purchaseWeaponSkin = (skin: number) => api.post('/game/arsenal/skins/purchase', { skin });
